import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Button from "../../../../components/base/button/button";
import Api from "../../../../../core/services/api/api";
import {DefaultPaginationInfo} from "../../../../../core/constants/enums";
import IconButton from "../../../../components/base/button/icon_button";
import {routeFunctions} from "../../../../../core/models/static/routes/app-routes";
import {VendorsListDataGridCellKeys} from "../../../../../core/constants/table/data-grid-column-keys";
import {Add, ImportExportOutlined, OpenInNew} from "@mui/icons-material";
import PartnerDataGrid, {
    DataGridColumnAlignments,
    DataGridColumnTypes,
    useDataGridSavedState,
} from "../../../../containers/data-grid";
import SearchInput from "../../../../components/base/input/search_input";
import useSearch from "../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../hooks/use-notebook-config";
import useAccessLevels from "../../../../hooks/use-access-levels";
import {Link, useHistory} from "react-router-dom";
import {Box, Typography} from "@mui/material";
import CreateVendorDialog from "../../../../components/dialogs/create-vendor";
import ImportExternalVendorDialog from "../../../../components/dialogs/import-external-vendor";
import useBehavioralAnalyticsTool from "../../../../hooks/use-behavioural-analytics-tool";
import {useTranslation} from "react-i18next";

const dataGridSaveConfig = {
    name: 'VendorsListViewDataGrid',
    version: 2,
};

const exportingChunkSize = 100;

const initialNewVendorDialog = {open: false};
const initialImportExternalVendorDialog = {open: false};

/**
 * Vendors list — live CleanAF/Vendor/List with useSearch, DataGrid, export, create/import dialogs.
 */
const VendorsListView = () => {
    const history = useHistory();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.vendors-list'});
    const operatorAccessLevels = useAccessLevels();
    const [newVendorDialog, setNewVendorDialog] = useState(initialNewVendorDialog);
    const [importExternalVendorDialog, setImportExternalVendorDialog] = useState(initialImportExternalVendorDialog);
    const [vendors, setVendors] = useState([]);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('vendors-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.vendorsList);
        configureBehavioralTool((tags) => tags.vendorsList);
    }, [configureBehavioralTool, configureNotebookTags]);

    const columns = useMemo(() => /** @type {DataGridColumn[]} */ ([
        {
            name: VendorsListDataGridCellKeys.navigate,
            alignment: DataGridColumnAlignments.center,
            type: DataGridColumnTypes.element,
            width: 0,
            sortable: false,
            resizable: false,
            pinned: true,
        },
        {
            title: t('columns.name'),
            name: VendorsListDataGridCellKeys.name,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            width: 180,
            pinned: true,
        },
        {
            title: t('columns.code'),
            name: VendorsListDataGridCellKeys.code,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
        },
        {
            title: t('columns.email'),
            name: VendorsListDataGridCellKeys.email,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
        },
        {
            title: t('columns.phone'),
            name: VendorsListDataGridCellKeys.phone,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
        },
        {
            title: t('columns.address'),
            name: VendorsListDataGridCellKeys.address,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            width: 200,
        },
        {
            title: t('columns.city'),
            name: VendorsListDataGridCellKeys.city,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
        },
        {
            title: t('columns.province'),
            name: VendorsListDataGridCellKeys.province,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            width: 120,
        },
        {
            title: t('columns.status'),
            name: VendorsListDataGridCellKeys.status,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
        },
    ]), [t]);

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Prepares search payload for CleanAF Vendor List (mirrors customers list).
     */
    const prepareSearchData = useCallback((args) => ({
        sorts: args.orderBy?.field
            ? [{
                property: args.orderBy.field,
                isDesc: args.orderBy.descending,
            }]
            : [],
        pagination: args.paginationInfo,
        keyword: args.filters?.keyword ?? null,
    }), []);

    /**
     * Searches vendors via CleanAF/Vendor/List.
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = prepareSearchData(args);
        const response = await Api.listVendorsV2(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setVendors(response.data?.items ?? response.data?.resultList ?? []);
            args.setPaginationInfo((prevState) => ({
                ...prevState,
                length: response?.data?.totalCount
                    ?? response?.data?.paginationInfo?.length
                    ?? response?.data?.pagination?.length
                    ?? 0,
            }));
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, prepareSearchData]);

    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => dataGridPageSize
        ? {...DefaultPaginationInfo, pageSize: dataGridPageSize}
        : DefaultPaginationInfo, [dataGridPageSize]);

    const searchData = useSearch(search, {
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
    });

    const searchByKeyword = useCallback((keywords) => {
        searchData.setFilters((prevState) => ({...prevState, keyword: keywords?.trim()}));
    }, [searchData]);

    const formatExportingData = useCallback((list) => ({
        title: t('exportTitle'),
        body: list?.map((item) => ({
            [t('columns.name')]: item.name ?? '',
            [t('columns.code')]: item.code ?? '',
            [t('columns.email')]: item.email ?? '',
            [t('columns.phone')]: item.phone ?? item.phoneNumber ?? '',
            [t('columns.address')]: item.address ?? '',
            [t('columns.city')]: item.city ?? '',
            [t('columns.province')]: item.province?.name ?? item.province ?? '',
            [t('columns.status')]: item.statusName ?? item.status ?? '',
        })) ?? [],
    }), [t]);

    const exportToCsv = useCallback(async () => {
        const paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        const entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo}, true);
            if (!res?.resultFlag)
                return;
            const page = res?.data?.items ?? res?.data?.resultList ?? [];
            if (!page.length)
                break;
            entries.push(...page);
            if (!isMounted())
                return;
            paginationInfo.currentPage++;
        }
        return formatExportingData(entries);
    }, [fetchEntries, formatExportingData, isMounted, searchData]);

    const onCreateVendor = useCallback(async (vendorId) => {
        if (vendorId) {
            return history.push(routeFunctions.vendor.accountInfo(vendorId));
        }
        searchData.setLoading(true);
        await search(searchData);
        if (!isMounted())
            return;
        searchData.setLoading(false);
    }, [history, isMounted, search, searchData]);

    const onExternalVendorImported = useCallback(async (vendor) => {
        if (vendor?.id) {
            return history.push(routeFunctions.vendor.accountInfo(vendor.id));
        }
        if (vendor) {
            searchData.setLoading(true);
            await search(searchData);
            if (!isMounted())
                return;
            searchData.setLoading(false);
        }
    }, [history, isMounted, search, searchData]);

    const rows = useMemo(() => vendors?.map((vendor) => ({
        key: vendor.id,
        data: vendor,
        cells: {
            [VendorsListDataGridCellKeys.navigate]: operatorAccessLevels.packages.base.viewVendorAccountInfo && (
                <IconButton
                    color={'secondary'}
                    component={Link}
                    to={routeFunctions.vendor.accountInfo(vendor.id)}
                >
                    <OpenInNew className={'icon normal'}/>
                </IconButton>
            ),
            [VendorsListDataGridCellKeys.name]: vendor.name,
            [VendorsListDataGridCellKeys.code]: vendor.code,
            [VendorsListDataGridCellKeys.email]: vendor.email,
            [VendorsListDataGridCellKeys.phone]: vendor.phone ?? vendor.phoneNumber ?? '',
            [VendorsListDataGridCellKeys.address]: vendor.address,
            [VendorsListDataGridCellKeys.city]: vendor.city,
            [VendorsListDataGridCellKeys.province]: vendor.province?.name ?? vendor.province ?? '',
            [VendorsListDataGridCellKeys.status]: vendor.statusName ?? vendor.status ?? '',
        },
    })) ?? [], [operatorAccessLevels.packages.base.viewVendorAccountInfo, vendors]);

    return (
        <>
            <div className={'d-flex w-100 justify-content-between align-items-center mt-3 mb-4'}>
                <Typography variant={'h6'} fontWeight={600}>
                    {t('title')}
                </Typography>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1}}>
                    {
                        operatorAccessLevels.packages.base.importExternalVendor &&
                        <Button
                            className={'d-flex align-items-center justify-content-center'}
                            variant={'outlined'}
                            color={'primary'}
                            onClick={() => setImportExternalVendorDialog({open: true})}
                        >
                            <ImportExportOutlined className={'mr-2'}/>
                            <p>{t('importExternal')}</p>
                        </Button>
                    }
                    {
                        operatorAccessLevels.packages.base.createNewVendor &&
                        <Button
                            className={'d-flex align-items-center justify-content-center'}
                            variant={'outlined'}
                            color={'primary'}
                            disabled={searchData.loading}
                            onClick={() => setNewVendorDialog({open: true})}
                        >
                            <Add className={'icon small'} style={{margin: '2px 0'}}/>
                            <p className={'ml-2'}>{t('addNew')}</p>
                        </Button>
                    }
                </Box>
            </div>
            <SearchInput
                loading={searchData.loading}
                autoSearch
                immediateSearch
                search={searchByKeyword}
                initialValue={searchData.filters?.keyword ?? undefined}
                label={t('searchLabel')}
            />
            <div className={'w-100 my-4'}/>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{columns}}
                columns={dataGridColumns}
                rows={rows}
                hideFooterActions
                state={{
                    sortBy: searchData.orderBy,
                    pagination: searchData.paginationInfo,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                getExportInfo={operatorAccessLevels.packages.base.exportVendorsList ? exportToCsv : undefined}
                onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo((prevState) => ({
                    ...prevState,
                    ...current,
                }))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo((prevState) => ({
                    ...prevState,
                    ...current,
                }))}
            />
            {
                operatorAccessLevels.packages.base.createNewVendor &&
                <CreateVendorDialog
                    open={newVendorDialog.open}
                    close={() => setNewVendorDialog(initialNewVendorDialog)}
                    onSuccess={onCreateVendor}
                />
            }
            {
                operatorAccessLevels.packages.base.importExternalVendor &&
                <ImportExternalVendorDialog
                    open={importExternalVendorDialog.open}
                    setOpen={() => setImportExternalVendorDialog(initialImportExternalVendorDialog)}
                    onSuccess={onExternalVendorImported}
                />
            }
        </>
    );
};

export default VendorsListView;
